import React, { useState, useEffect, useRef, useContext } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AuthContext } from '../AuthContext';
import StatusIndicator from './StatusIndicator';
import ProfileUpload from './ProfileUpload';
import './Header.css';

const Header = ({ user, profileImage }) => {
    const { isAdmin } = useContext(AuthContext);
    const [showUpload, setShowUpload] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isPro, setIsPro] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribe = onSnapshot(
            doc(db, 'users', user.uid),
            (doc) => {
                if (doc.exists()) {
                    const userData = doc.data();
                    
                    // Check for new role-based system first, then fallback to legacy boolean
                    const isProUser = userData.role === 'pro_user' || userData.isPro === true;
                    
                    setIsPro(isProUser);
                    setSubscriptionStatus(userData.subscriptionStatus);
                    setSubscriptionEndDate(userData.subscriptionEndDate);
                }
            },
            (error) => {
                console.error("Error fetching user status:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Format subscription end date for display
    const formattedEndDate = subscriptionEndDate 
        ? new Date(subscriptionEndDate * 1000).toLocaleDateString() 
        : null;

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setShowDropdown(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleStatusToggle = async () => {
        if (!isAdmin) return;
        
        try {
            const userRef = doc(db, 'users', user.uid);
            
            // Get current user data to determine current role
            const userDoc = await getDoc(userRef);
            const currentUserData = userDoc.data();
            const currentRole = currentUserData?.role || 'user';
            
            // Cycle through roles: user -> pro_user -> admin -> user
            let newRole;
            if (currentRole === 'user') {
                newRole = 'pro_user';
            } else if (currentRole === 'pro_user') {
                newRole = 'admin';
            } else {
                newRole = 'user';
            }
            
            await updateDoc(userRef, {
                role: newRole
            });
            
            // Admin role toggle completed
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    const handleUnsubscribe = async () => {
        if (window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.')) {
            try {
                setLoading(true);
                
                // Get the functions instance
                const functions = getFunctions();
                
                // Create a callable reference to your Cloud Function
                const cancelSubscriptionFunction = httpsCallable(functions, 'cancelSubscription');
                
                // Call the function without any parameters
                const result = await cancelSubscriptionFunction();
                
                // Don't update isPro state since they still have access
                // Just update the subscription status
                
                // Format the end date for display
                const endDate = new Date(result.data.endDate * 1000).toLocaleDateString();
                
                alert(`Your subscription has been canceled. You will continue to have Pro access until ${endDate}.`);
                
            } catch (error) {
                console.error('Unsubscribe error:', error);
                alert('There was an error canceling your subscription. Please try again later.');
            } finally {
                setLoading(false);
                setShowDropdown(false);
            }
        }
    };
    
    const handleProfileClick = () => {
        setShowUpload(true);
        setShowDropdown(false);
    };

    return (
        <header className="main-header">
            <div className="left-section">
                <div className="profile-section">
                    <div 
                        className="profile-container"
                        onClick={() => setShowUpload(!showUpload)}
                    >
                        {profileImage ? (
                            <img 
                                src={profileImage} 
                                alt="Profile" 
                                className="header-profile-image"
                            />
                        ) : (
                            <div className="profile-placeholder">
                                {user?.email?.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="edit-overlay">
                            <span>Edit</span>
                        </div>
                    </div>
                    {showUpload && (
                        <ProfileUpload 
                            user={user} 
                            onClose={() => setShowUpload(false)}
                        />
                    )}
                </div>
            </div>

            <div className="center-section">
                <h1 
                    className="site-title"
                    style={{ color: isAdmin ? '#8A2BE2' : 'white' }}
                >
                    DANNYTOPIA
                </h1>
            </div>

            <div className="right-section">
                <div className="status-dropdown-container" ref={dropdownRef}>
                    <div 
                        className="status-button"
                        onClick={() => setShowDropdown(!showDropdown)}
                    >
                        <StatusIndicator user={user} />
                        <span className="dropdown-arrow">▼</span>
                    </div>
                    {showDropdown && (
                        <>
                            <div 
                                className="header-modal-backdrop"
                                onClick={() => setShowDropdown(false)}
                            >
                                <div 
                                    className="header-modal-content"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="header-modal-header">
                                        <div>
                                            User Menu
                                        </div>
                                        <button 
                                            className="header-modal-close-btn"
                                            onClick={() => setShowDropdown(false)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <div className="header-modal-body">
                                
                                        <button 
                                            className="header-modal-button header-profile-button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleProfileClick();
                                            }}
                                        >
                                            Profile
                                        </button>
                                
                                        {isAdmin && (
                                            <button 
                                                className="header-modal-button header-toggle-button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleStatusToggle();
                                                }}
                                            >
                                                Toggle Role (Current: {isPro ? 'Pro' : 'Basic'})
                                            </button>
                                        )}
                                
                                        {isPro && subscriptionStatus === 'active' && (
                                            <button 
                                                className="header-modal-button header-unsubscribe-button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleUnsubscribe();
                                                }}
                                                disabled={loading}
                                            >
                                                {loading ? 'Processing...' : 'Cancel Subscription'}
                                            </button>
                                        )}
                                
                                        {isPro && subscriptionStatus === 'pending_cancellation' && (
                                            <div className="header-subscription-notice">
                                                Sub Ends: {formattedEndDate}
                                            </div>
                                        )}
                                
                                        <button 
                                            className="header-modal-button header-signout-button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSignOut();
                                            }}
                                        >
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
